import { Component, computed, inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CiscoFields,
  CiscoMigrationSite,
  CiscoMigrationSiteSourceField,
  CiscoMigrationSiteValueMapping,
  CiscoPbxExport,
  CiscoPhoneNumberReport,
  CucAutoAttendantsReport,
  CucmHuntList,
  DeviceUsageTypeV2,
  JobState,
  JobStatus,
  LineUsageTypeV2,
  MigrationComplexityScore,
  MigrationStatusV2,
  NumberReportEntry,
  SuggestedProductV2,
} from '../../../shared/models/generated/ziroModels';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles } from '../../../button/button.component';
import { combineLatest, forkJoin, map, Subscription, switchMap } from 'rxjs';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ChartData, DisabledChartText } from '../../migrations-cards/migrations-card-abstract.directive';
import { MigrationsCucmHuntListsContext } from '../../services/cisco/migrations-cucm-hunt-lists.context';
import { MigrationsCucmNumberReportContext } from '../../services/cisco/migrations-cucm-number-report.context';
import moment from 'moment';
import {
  COLOR_PALETTE,
  MigrationsService,
  UiDeviceType,
  UiLineType,
  UiMigrationStatus,
} from '../../services/migrations.service';
import { MigrationsCucAutoAttendantsContext } from '../../services/cisco/migrations-cuc-auto-attendants.context';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';
import { CustomColor, MigrationsDashboardService } from '../../services/migrations-dashboard.service';
import { TranslateService } from '@ngx-translate/core';
import { MigrationsCiscoSiteManagementContext } from '../../site-migration/migrations-cisco-site-management/migrations-cisco-site-management.context';
import { MigrationsCiscoSourceFieldMappingContext } from '../../site-migration/migrations-cisco-source-field-mapping/migrations-cisco-source-field-mapping.context';
import {
  MigrationsSiteProgressService,
  SiteProgress,
  SiteProgressBucketDefinition,
  SiteProgressReport,
} from '../../services/migrations-site-progress.service';

interface SiteConfigurationState {
  sites: CiscoMigrationSite[];
  dnReport: CiscoPhoneNumberReport;
  sourceField: CiscoMigrationSiteSourceField;
  mappings: CiscoMigrationSiteValueMapping[];
}

export type WidgetFilter = 'lineType' | 'deviceType' | 'callingProduct' | 'migrationStatus' | 'site';

@Component({
  selector: 'ziro-migrations-cisco-dashboard',
  templateUrl: 'migrations-cisco-dashboard.component.html',
  styleUrls: ['../../migrations.component.scss'],
  providers: [
    MigrationsService,
    MigrationsDashboardService,
    MigrationsSiteProgressService,
    MigrationsCiscoSiteManagementContext,
    MigrationsCiscoSourceFieldMappingContext,
  ],
  standalone: false,
})
export class MigrationsCiscoDashboardComponent implements OnInit, OnDestroy {
  private readonly _refreshButtonId = 'migrations-cisco-reports-list-refresh';
  private _siteConfigurationButtonId: string = 'migrations-cisco-site-configuration';

  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _translateService = inject(TranslateService);
  private _dashboardService = inject(MigrationsDashboardService);
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _migrationsCucmHuntListsContext = inject(MigrationsCucmHuntListsContext);
  private _migrationsCucmNumberReportContext = inject(MigrationsCucmNumberReportContext);
  private _migrationsCucAutoAttendantsContext: MigrationsCucAutoAttendantsContext | null = null;
  private _injector = inject(Injector);
  private _siteProgressService = inject(MigrationsSiteProgressService);
  private _siteManagementContext = inject(MigrationsCiscoSiteManagementContext);
  private _sourceFieldMappingContext = inject(MigrationsCiscoSourceFieldMappingContext);
  private _configuredSites: CiscoMigrationSite[] = [];

  readonly MIN_CDR_DAYS = 30;
  private _assessmentUuid: string;
  private _assessment: CiscoPbxExport;
  private _subscription = new Subscription();
  private _dnReport: CiscoPhoneNumberReport;
  private _huntLists: CucmHuntList[];
  private _dnJobStatus: JobStatus;
  private _huntGroupJobStatus: JobStatus;
  private _autoAttendantJobStatus: JobStatus | null;
  private _isManualRefreshPending = false;
  private _tenantId: string;

  colorScheme = this._dashboardService.createDefaultColorScheme();
  autoAttendantCustomColors: CustomColor[] = [{ name: 'require investigation', value: COLOR_PALETTE.danger }];
  usageTypeCustomColors = this._dashboardService.usageTypeCustomColors;
  deviceTypeCustomColors = this._dashboardService.deviceTypeCustomColors;
  migrationStatusCustomColors = this._dashboardService.migrationStatusCustomColors;
  sitesByProgressCustomColors: CustomColor[] = MigrationsSiteProgressService.DONUT_BUCKETS.map(
    (definition: SiteProgressBucketDefinition): CustomColor => ({
      name: this._translateService.instant(definition.labelKey),
      value: definition.color,
    }),
  );

  sitesByProgressData: ChartData[] = [];
  sitesByProgressFooterNote: string = '';
  isSiteConfigurationComplete: boolean = false;
  siteConfigurationDisabledText: DisabledChartText = {
    header: 'tkey;migrations.site_progress.not_configured.header',
    description: 'tkey;migrations.site_progress.not_configured.description',
  };
  migrationStatusData: ChartData[] = [];
  usageTypeData: ChartData[] = [];
  deviceTypeData: ChartData[] = [];
  huntListData: ChartData[] = [];
  suggestedProductData: ChartData[] = [];
  autoAttendantsData: ChartData[] = [];
  suggestedProductsDisabledText: DisabledChartText;
  autoAttendantsDisabledText: DisabledChartText;
  isLoading = true;
  isDnReportWidgetError = false;
  isHuntListError = false;
  cdrDiffDays: number = this.MIN_CDR_DAYS + 1;
  isCdrFileUploaded: boolean;
  isUnityFileUploaded: boolean;
  isAutoAttendantReportWidgetError = false;

  isDnReportDataRefreshing = signal<boolean>(true);
  isSitesByProgressRefreshing = signal<boolean>(true);
  isHuntListRefreshing = signal<boolean>(true);
  isAutoAttendantRefreshing = signal<boolean>(true);

  private _isRefreshing = computed(
    () => this.isDnReportDataRefreshing() || this.isHuntListRefreshing() || this.isAutoAttendantRefreshing(),
  );

  ngOnInit(): void {
    this._assessmentUuid = this._route.snapshot.paramMap.get('id');
    this._tenantId = this._route.snapshot.paramMap.get('tenantId');
    this._initBottomNav();
    this._updateStates();
    this._initContexts();
    this._initSiteConfiguration();
  }

  onSitesByProgressClick(bucketLabel: string): void {
    if (bucketLabel) {
      this._router.navigate(['sites-overview', encodeURIComponent(bucketLabel)], { relativeTo: this._route });
    } else {
      this._router.navigate(['sites-overview'], { relativeTo: this._route });
    }
  }

  onConfigureSitesClick(): void {
    this._router.navigate(['site-configuration'], { relativeTo: this._route });
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  onDnReportFilter(filter: string, filterType: WidgetFilter): void {
    // This navigation link for legend items is defined on the data passed into the child
    if (filter) {
      this._router.navigate(['cucm-directory-numbers-report', `${filterType}=${encodeURIComponent(filter)}`], {
        relativeTo: this._route,
      });
    } else {
      this._router.navigate(['cucm-directory-numbers-report'], { relativeTo: this._route });
    }
  }

  onHuntListClick(): void {
    this._router.navigate(['cucm-hunt-lists-report'], { relativeTo: this._route });
  }

  onRefreshClick(): void {
    this._refreshData();
  }

  onAutoAttendantReportFilter(filter: string): void {
    if (filter) {
      this._router.navigate(['cuc-auto-attendants-report', encodeURIComponent(filter)], {
        relativeTo: this._route,
      });
    } else {
      this._router.navigate(['cuc-auto-attendants-report'], { relativeTo: this._route });
    }
  }

  private _initContexts(): void {
    const sub = this._migrationsAssessmentsContext.state$
      .pipe(
        switchMap((migrations) => {
          this._assessment = migrations.ciscoPbxExports.find((migration) => migration.id === this._assessmentUuid);
          this.isUnityFileUploaded = this._assessment.isCucExportPresent;
          this.isCdrFileUploaded = this._assessment.isCucmCdrDumpPresent;
          this._setCallingProductDisabledText();

          if (this.isUnityFileUploaded && !this._migrationsCucAutoAttendantsContext) {
            this._migrationsCucAutoAttendantsContext = this._injector.get(MigrationsCucAutoAttendantsContext);
          }

          if (!this.isUnityFileUploaded) {
            this.autoAttendantsDisabledText = {
              header:
                'tkey;migrations.cisco.auto_attendants_report.dashboard.auto_attendant.no_unity_file.disabled.header',
              description:
                'tkey;migrations.cisco.auto_attendants_report.dashboard.auto_attendant.no_unity_file.disabled.description',
            };
          }

          const baseObservables = [
            this._migrationsCucmNumberReportContext.jobState$,
            this._migrationsCucmHuntListsContext.jobState$,
          ];

          const jobStatusObservables = this.isUnityFileUploaded
            ? [...baseObservables, this._migrationsCucAutoAttendantsContext.jobState$]
            : baseObservables;

          return combineLatest(jobStatusObservables);
        }),
      )
      .subscribe(([dnsJobStatus, huntGroupsJobStatus, autoAttendantJobStatus]) =>
        this._pollJobStatuses(dnsJobStatus, huntGroupsJobStatus, autoAttendantJobStatus),
      );
    this._subscription.add(sub);
  }

  private _initSiteConfiguration(): void {
    const subscription = combineLatest([
      this._siteManagementContext.state$,
      this._migrationsCucmNumberReportContext.state$,
    ])
      .pipe(
        switchMap(([sites, dnReport]: [CiscoMigrationSite[], CiscoPhoneNumberReport]) =>
          this._sourceFieldMappingContext.refreshAll().pipe(
            switchMap(() =>
              combineLatest([this._sourceFieldMappingContext.sourceField$, this._sourceFieldMappingContext.mappings$]),
            ),
            map(
              ([sourceField, mappings]: [
                CiscoMigrationSiteSourceField,
                CiscoMigrationSiteValueMapping[],
              ]): SiteConfigurationState => ({ sites, dnReport, sourceField, mappings }),
            ),
          ),
        ),
      )
      .subscribe((siteConfigurationState: SiteConfigurationState) => {
        this._configuredSites = siteConfigurationState.sites;
        this.isSiteConfigurationComplete =
          siteConfigurationState.sites.length > 0 &&
          !!siteConfigurationState.sourceField &&
          siteConfigurationState.mappings.length > 0;

        if (this.isSiteConfigurationComplete) {
          this._setSitesByProgressData(siteConfigurationState.dnReport);
        } else {
          this.sitesByProgressData = [];
          this.sitesByProgressFooterNote = '';
        }

        this.isSitesByProgressRefreshing.set(false);
      });
    this._subscription.add(subscription);
  }

  private _setSitesByProgressData(dnReport: CiscoPhoneNumberReport): void {
    const siteNamesBySiteId = new Map<string, string>(
      this._configuredSites.map((site: CiscoMigrationSite): [string, string] => [site.siteId, site.name]),
    );
    const progressReport: SiteProgressReport = this._siteProgressService.aggregate(dnReport.entries, siteNamesBySiteId);

    this.sitesByProgressData = MigrationsSiteProgressService.DONUT_BUCKETS.map(
      (definition: SiteProgressBucketDefinition): ChartData => {
        const label = this._translateService.instant(definition.labelKey);
        return {
          name: label,
          value: progressReport.sites.filter((site: SiteProgress): boolean => site.bucket === definition.bucket).length,
          link: ['sites-overview', encodeURIComponent(label)],
        };
      },
    );

    this.sitesByProgressFooterNote = progressReport.unassignedDirectoryNumberCount
      ? this._translateService.instant('tkey;migrations.site_progress.unassigned_directory_numbers.note', {
          count: progressReport.unassignedDirectoryNumberCount,
        })
      : '';
  }

  private _refreshData(): void {
    if (this.isCdrFileUploaded && !this.cdrDiffDays) {
      this.cdrDiffDays = this.MIN_CDR_DAYS + 1;
    }
    this._dnReport = null;
    this._huntLists = null;
    this.migrationStatusData = [];
    this.usageTypeData = [];
    this.deviceTypeData = [];
    this.huntListData = [];
    this.suggestedProductData = [];
    this.autoAttendantsData = [];

    this._isManualRefreshPending = true;
    this.isSitesByProgressRefreshing.set(true);
    this._updateStates();

    const requests = [];

    if (this._dnJobStatus?.jobState === JobState.IDLE) {
      requests.push(this._migrationsCucmNumberReportContext.refresh());
    }

    if (this._huntGroupJobStatus?.jobState === JobState.IDLE) {
      requests.push(this._migrationsCucmHuntListsContext.refresh());
    }

    if (this.isUnityFileUploaded && this._autoAttendantJobStatus?.jobState === JobState.IDLE) {
      requests.push(this._migrationsCucAutoAttendantsContext.refresh());
    }

    forkJoin(requests).subscribe({
      next: () => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.REFRESH,
          'tkey;migrations.dashboard.refresh_toast.title',
          'tkey;migrations.dashboard.refresh_toast.message',
        );
      },
    });
  }

  private _pollJobStatuses(
    dnsJobStatus: JobStatus,
    huntGroupsJobStatus: JobStatus,
    autoAttendantJobStatus?: JobStatus,
  ): void {
    this._initBreadcrumbs();
    this.isLoading = false;

    this._dnJobStatus = dnsJobStatus;
    this._huntGroupJobStatus = huntGroupsJobStatus;
    this._autoAttendantJobStatus = this.isUnityFileUploaded ? autoAttendantJobStatus : null;
    this._isManualRefreshPending = false;

    const shouldGetDnReport = MigrationsService.shouldGetDnReport(dnsJobStatus, huntGroupsJobStatus);
    if (shouldGetDnReport) {
      this.isDnReportWidgetError = MigrationsService.isErroredStatus(dnsJobStatus, huntGroupsJobStatus);
    }

    const isHuntGroupIdle = MigrationsService.isHuntGroupIdle(huntGroupsJobStatus);
    if (isHuntGroupIdle) {
      this.isHuntListError = MigrationsService.isErroredHuntGroupStatus(huntGroupsJobStatus);
    }

    const isAutoAttendantReportIdle =
      this.isUnityFileUploaded && MigrationsService.isAutoAttendantReportIdle(autoAttendantJobStatus);
    if (isAutoAttendantReportIdle) {
      this.isAutoAttendantReportWidgetError = autoAttendantJobStatus.error !== null;
    }

    this._updateStates();

    if (shouldGetDnReport && !this.isDnReportWidgetError) {
      this._getDnReportData();
    }

    if (isHuntGroupIdle && !this.isHuntListError) {
      this._getHuntListData();
    }

    if (isAutoAttendantReportIdle && !this.isAutoAttendantReportWidgetError) {
      this._getAutoAttendantsData();
    }
  }

  private _updateStates(): void {
    const isDnJobRunning = !this._dnJobStatus || this._dnJobStatus.jobState !== JobState.IDLE;
    const isHuntGroupJobRunning = !this._huntGroupJobStatus || this._huntGroupJobStatus.jobState !== JobState.IDLE;
    const isAutoAttendantJobRunning =
      !!this._autoAttendantJobStatus && this._autoAttendantJobStatus.jobState !== JobState.IDLE;

    const isDnReportRefreshing =
      this._isManualRefreshPending ||
      isDnJobRunning ||
      isHuntGroupJobRunning ||
      (!this.isDnReportWidgetError && !this._dnReport);

    const isHuntListRefreshing = this._isManualRefreshPending || isHuntGroupJobRunning;

    const isAutoAttendantRefreshing =
      this.isUnityFileUploaded && (this._isManualRefreshPending || isAutoAttendantJobRunning);

    this.isDnReportDataRefreshing.set(isDnReportRefreshing);
    this.isHuntListRefreshing.set(isHuntListRefreshing);
    this.isAutoAttendantRefreshing.set(isAutoAttendantRefreshing);

    this._dashboardService.setRefreshButtonPendingState(this._refreshButtonId, this._isRefreshing());
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: this._siteConfigurationButtonId,
        state: {
          pending: false,
          buttonDisableState: { disabled: this._isRefreshing(), tooltipKey: '' },
        },
      }),
    );
  }

  private _getDnReportData(): void {
    const subscription = this._migrationsCucmNumberReportContext.state$.subscribe(
      (dnReport: CiscoPhoneNumberReport) => {
        this._dnReport = dnReport;
        this._updateStates();

        this._calculateCdrDays();

        if (
          this._dnReport &&
          this._huntLists &&
          !this.usageTypeData.length &&
          !this.deviceTypeData.length &&
          !this.suggestedProductData.length
        ) {
          this._setChartData(dnReport);
        }
      },
    );
    this._subscription.add(subscription);
  }

  private _calculateCdrDays(): void {
    const oldestMoment = moment(new Date(this._dnReport.oldestCdrTimestamp));
    const newestMoment = moment(new Date(this._dnReport.mostRecentCdrTimestamp));
    this.cdrDiffDays = newestMoment.diff(oldestMoment, 'days');

    this._setCallingProductDisabledText();
  }

  private _setCallingProductDisabledText(): void {
    if (!this.isCdrFileUploaded) {
      this.cdrDiffDays = 0;
    }

    this.suggestedProductsDisabledText = this.isCdrFileUploaded
      ? {
          header:
            'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.insufficient_cdr_days.disabled.header',
          description:
            'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.disabled.description',
        }
      : {
          header:
            'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.no_cdrs.disabled.header',
          description:
            'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.disabled.description',
        };
  }

  private _getHuntListData(): void {
    const subscription = this._migrationsCucmHuntListsContext.state$.subscribe((huntLists: CucmHuntList[]) => {
      this._huntLists = huntLists;
      this._setHuntListData(huntLists);

      if (
        this._dnReport &&
        this._huntLists &&
        !this.usageTypeData.length &&
        !this.deviceTypeData.length &&
        !this.suggestedProductData.length
      ) {
        this._setChartData(this._dnReport);
      }
    });
    this._subscription.add(subscription);
  }

  private _setChartData(dnReport: CiscoPhoneNumberReport): void {
    // Suggested Calling Product
    const sharedCallingLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.shared_calling.label',
    );
    const perMinuteLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.per_minute.label',
    );
    const bundledMinutesLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.bundled_minutes.label',
    );

    let sharedCallingCount = 0;
    let perMinuteCount = 0;
    let bundledMinutesCount = 0;

    dnReport.entries.forEach((entry) => {
      switch (entry.numberInsights.suggestedProduct) {
        case SuggestedProductV2.SHARED_CALLING: {
          sharedCallingCount++;
          break;
        }
        case SuggestedProductV2.PER_MINUTE: {
          perMinuteCount++;
          break;
        }
        case SuggestedProductV2.BUNDLED_MINUTES: {
          bundledMinutesCount++;
          break;
        }
        default:
          break;
      }
    });

    this.migrationStatusData = this._createMigrationStatusChartData(dnReport.entries);
    this.usageTypeData = this._createUsageTypeChartData(dnReport.entries);
    this.deviceTypeData = this._createDeviceTypeChartData(dnReport.entries);

    this.suggestedProductData = [
      {
        name: sharedCallingLabel,
        value: sharedCallingCount,
        link: ['cucm-directory-numbers-report', `${'callingProduct'}=${encodeURIComponent(sharedCallingLabel)}`],
      },
      {
        name: perMinuteLabel,
        value: perMinuteCount,
        link: ['cucm-directory-numbers-report', `${'callingProduct'}=${encodeURIComponent(perMinuteLabel)}`],
      },
      {
        name: bundledMinutesLabel,
        value: bundledMinutesCount,
        link: ['cucm-directory-numbers-report', `${'callingProduct'}=${encodeURIComponent(bundledMinutesLabel)}`],
      },
    ];
  }

  private _setAutoAttendantChartData(autoAttendants: CucAutoAttendantsReport): void {
    const simpleScoreLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.simple_score.label',
    );
    const complexScoreLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.complex_score.label',
    );
    const requireInvestigationScoreLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.require_investigation_score.label',
    );

    const allHandlers = [
      ...(autoAttendants.callHandlers || []),
      ...(autoAttendants.directoryHandlers || []),
      ...(autoAttendants.interviewHandlers || []),
    ];

    const scoreCounts = allHandlers.reduce(
      (counts, handler) => {
        switch (handler.migrationComplexityScore) {
          case MigrationComplexityScore.SIMPLE:
            counts.simple++;
            break;
          case MigrationComplexityScore.COMPLEX:
            counts.complex++;
            break;
          case MigrationComplexityScore.REQUIRE_INVESTIGATION:
            counts.requireInvestigation++;
            break;
        }
        return counts;
      },
      { simple: 0, complex: 0, requireInvestigation: 0 },
    );

    this.autoAttendantsData = [
      {
        name: simpleScoreLabel,
        value: scoreCounts.simple,
        tooltipText: this._translateService.instant(
          'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.simple_score.tooltip.text',
        ),
        link: ['cuc-auto-attendants-report', encodeURIComponent(simpleScoreLabel)],
      },
      {
        name: complexScoreLabel,
        value: scoreCounts.complex,
        tooltipText: this._translateService.instant(
          'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.complex_score.tooltip.text',
        ),
        link: ['cuc-auto-attendants-report', encodeURIComponent(complexScoreLabel)],
      },
      {
        name: requireInvestigationScoreLabel,
        value: scoreCounts.requireInvestigation,
        tooltipText: this._translateService.instant(
          'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.require_investigation_score.tooltip.text',
        ),
        link: ['cuc-auto-attendants-report', encodeURIComponent(requireInvestigationScoreLabel)],
      },
    ];
  }

  private _setHuntListData(huntLists: CucmHuntList[]): void {
    const allLineGroupMembers = huntLists.flatMap((obj) => obj.lineGroupMemberships);
    const uniqueLineGroupMemberships = Array.from(
      new Map(allLineGroupMembers.map((item) => [item.lineGroupName, item])).values(),
    );
    const allHuntPilots = huntLists.flatMap((obj) => obj.huntPilots);
    const huntListsCount = huntLists.length;

    this.huntListData = [
      {
        name: this._translateService.instant(
          huntListsCount === 1
            ? 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.hunt_lists.label.singular'
            : 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.hunt_lists.label.plural',
        ),
        value: huntListsCount,
      },
      {
        name: this._translateService.instant(
          uniqueLineGroupMemberships.length === 1
            ? 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.line_groups.label.singular'
            : 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.line_groups.label.plural',
        ),
        value: uniqueLineGroupMemberships.length,
      },
      {
        name: this._translateService.instant(
          allHuntPilots.length === 1
            ? 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.hunt_pilots.label.singular'
            : 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.hunt_pilots.label.plural',
        ),
        value: allHuntPilots.length,
      },
    ];
  }

  private _initBreadcrumbs(): void {
    this._dashboardService.updateDashboardBreadcrumbs(this._tenantId, this._assessment.name);
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'migrations-cisco-reports-list-back',
          dataAutomation: 'migrations-cisco-reports-list-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => this._router.navigate(['../../'], { relativeTo: this._route }),
        },
        {
          id: this._siteConfigurationButtonId,
          dataAutomation: 'migrations-cisco-site-configuration-button',
          label: 'tkey;migrations.site_configuration.button.label',
          buttonClass: ButtonStyles.PRIMARY,
          icon: ZiroIcons.GEAR_COMPLEX,
          state: {
            pending: false,
            buttonDisableState: { disabled: true },
          },
          cb: () => this.onConfigureSitesClick(),
        },
        {
          id: this._refreshButtonId,
          dataAutomation: 'migrations-cisco-reports-list-refresh-button',
          label: 'tkey;migrations.dashboard.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: false,
            buttonDisableState: { disabled: false },
          },
          cb: () => this.onRefreshClick(),
        },
      ]),
    );
  }

  private _getAutoAttendantsData(): void {
    const subscription = this._migrationsCucAutoAttendantsContext.state$.subscribe(
      (autoAttendants: CucAutoAttendantsReport) => {
        if (!this.autoAttendantsData.length) {
          this._setAutoAttendantChartData(autoAttendants);
        }
      },
    );
    this._subscription.add(subscription);
  }

  private _calculateTypeCounts(
    entries: NumberReportEntry<CiscoFields>[],
    type: 'lineUsageType' | 'deviceUsageType',
  ): Map<LineUsageTypeV2 | DeviceUsageTypeV2, number> {
    const counts = new Map<LineUsageTypeV2 | DeviceUsageTypeV2, number>();

    const values = type === 'lineUsageType' ? LineUsageTypeV2 : DeviceUsageTypeV2;

    Object.values(values).forEach((usageType) => {
      counts.set(usageType, 0);
    });

    entries.forEach((entry) => {
      const insightType = entry.numberInsights[type];
      counts.set(insightType, (counts.get(insightType) || 0) + 1);
    });

    return counts;
  }

  private _createMigrationStatusChartData(entries: NumberReportEntry<CiscoFields>[]): ChartData[] {
    const statusCounts = new Map<MigrationStatusV2, number>();

    Object.values(MigrationStatusV2).forEach((status) => {
      statusCounts.set(status, 0);
    });

    entries.forEach((entry) => {
      statusCounts.set(entry.migrationStatus, (statusCounts.get(entry.migrationStatus) || 0) + 1);
    });

    const migrationStatusLabels = new Map<MigrationStatusV2, string>([
      [MigrationStatusV2.MIGRATED, UiMigrationStatus.MIGRATED],
      [MigrationStatusV2.IGNORED, UiMigrationStatus.IGNORED],
      [MigrationStatusV2.NOT_READY, UiMigrationStatus.NOT_READY],
      [MigrationStatusV2.READY, UiMigrationStatus.READY],
      [MigrationStatusV2.STAGED, UiMigrationStatus.STAGED],
    ]);

    return Array.from(statusCounts.entries())
      .map(([status, count]) => ({
        name: migrationStatusLabels.get(status),
        value: count,
        link: [
          'cucm-directory-numbers-report',
          `${'migrationStatus'}=${encodeURIComponent(migrationStatusLabels.get(status))}`,
        ],
      }))
      .sort((a, b) => b.value - a.value);
  }

  private _createUsageTypeChartData(entries: NumberReportEntry<CiscoFields>[]): ChartData[] {
    const usageTypeCounts = this._calculateTypeCounts(entries, 'lineUsageType') as Map<LineUsageTypeV2, number>;
    const usageTypeLabels = new Map<LineUsageTypeV2, string>([
      [LineUsageTypeV2.USER, UiLineType.USER],
      [LineUsageTypeV2.SHARED_DEVICE, UiLineType.SHARED_DEVICE],
      [LineUsageTypeV2.CONTACT_CENTER_AGENT, UiLineType.CONTACT_CENTER_AGENT],
      [LineUsageTypeV2.MULTI_LINE_USER, UiLineType.MULTI_LINE_USER],
      [LineUsageTypeV2.ANALOG, UiLineType.ANALOG],
      [LineUsageTypeV2.VOICE_APP, UiLineType.VOICE_APP],
      [LineUsageTypeV2.INACTIVE, UiLineType.INACTIVE],
      [LineUsageTypeV2.SHARED_LINE, UiLineType.SHARED_LINE],
      [LineUsageTypeV2.AUTO_ATTENDANT, UiLineType.AUTO_ATTENDANT],
      [LineUsageTypeV2.ORPHAN, UiLineType.ORPHAN],
      [LineUsageTypeV2.UNCLASSIFIED, UiLineType.UNCLASSIFIED],
    ]);

    return Array.from(usageTypeCounts.entries())
      .map(([usageType, count]) => ({
        name: usageTypeLabels.get(usageType) || usageType.toString(),
        value: count,
        link: [
          'cucm-directory-numbers-report',
          `${'lineType'}=${encodeURIComponent(usageTypeLabels.get(usageType) || usageType.toString())}`,
        ],
      }))
      .sort((a, b) => b.value - a.value);
  }

  private _createDeviceTypeChartData(entries: NumberReportEntry<CiscoFields>[]): ChartData[] {
    const deviceTypeCounts = this._calculateTypeCounts(entries, 'deviceUsageType') as Map<DeviceUsageTypeV2, number>;
    const deviceTypeLabels = new Map<DeviceUsageTypeV2, string>([
      [DeviceUsageTypeV2.SOFTPHONE_ONLY, UiDeviceType.SOFTPHONE_ONLY],
      [DeviceUsageTypeV2.DESKPHONE, UiDeviceType.DESKPHONE],
      [DeviceUsageTypeV2.CTI_PORT, UiDeviceType.CTI_PORT],
      [DeviceUsageTypeV2.ANALOG_FAX, UiDeviceType.ANALOG_FAX],
      [DeviceUsageTypeV2.ANALOG_MODEM, UiDeviceType.ANALOG_MODEM],
      [DeviceUsageTypeV2.ANALOG_OTHER, UiDeviceType.ANALOG_OTHER],
      [DeviceUsageTypeV2.HOT_DESKING, UiDeviceType.HOT_DESKING],
      [DeviceUsageTypeV2.MIXED, UiDeviceType.MIXED],
      [DeviceUsageTypeV2.NO_DEVICE, UiDeviceType.NO_DEVICE],
    ]);

    return Array.from(deviceTypeCounts.entries())
      .map(([deviceType, count]) => ({
        name: deviceTypeLabels.get(deviceType) || deviceType.toString(),
        value: count,
        link: [
          'cucm-directory-numbers-report',
          `${'deviceType'}=${encodeURIComponent(deviceTypeLabels.get(deviceType) || deviceType.toString())}`,
        ],
      }))
      .sort((a, b) => b.value - a.value);
  }
}
